import { useCallback, useEffect, useState } from 'react';
import { useEvent } from 'react-use';
import { usePathname } from 'next/navigation';
import useIsBrowser from 'hooks/useIsBrowser';
import getQuickViewStateFromProduct from './getQuickViewStateFromProduct';
const SYNDECA_VIEWER_HOST = 'viewer.syndeca.com';
const SYNDECA_MESSAGE_PROTOCOLS = ['https:', 'http:'];
const isSyndecaOrigin = origin => {
  try {
    const {
      protocol,
      hostname
    } = new URL(origin);
    return hostname === SYNDECA_VIEWER_HOST && SYNDECA_MESSAGE_PROTOCOLS.includes(protocol);
  } catch {
    return false;
  }
};
const useQuickViewModalState = () => {
  const isBrowser = useIsBrowser();
  const pathname = usePathname();
  const [isOpen, setIsOpen] = useState(false);
  const [slug, setSlug] = useState(null);
  const [path, setPath] = useState(null);
  const [productVariation, setProductVariation] = useState(null);
  const openForProduct = useCallback(product => {
    const quickViewState = getQuickViewStateFromProduct(product);
    if (!quickViewState) {
      return;
    }
    setSlug(quickViewState.slug);
    setPath(quickViewState.path);
    setProductVariation(quickViewState.variantSku);
    setIsOpen(true);
  }, []);

  // Catalog/lookbook viewer: JSON postMessage from the Syndeca iframe.
  const onLookbookQuickViewClick = useCallback(event => {
    if (!isSyndecaOrigin(event?.origin)) {
      return;
    }

    // All Lookbooks messages are json encoded and will need to be parsed
    try {
      const message = JSON.parse(event?.data);
      if (message.action === 'quickView') {
        openForProduct(message.product);
      }
    } catch {
      // Ignore JSON parse errors, this is an indication that the message was
      // not sent by Lookbooks
    }
  }, [openForProduct]);

  // Shop Layer: `syndecaProductClick` CustomEvent dispatched on document by the
  // Syndeca universal loader, which runs in our own page rather than an iframe.
  // There is no origin to check — the loader is same-origin by construction.
  const onShopLayerProductClick = useCallback(event_0 => {
    openForProduct(event_0?.detail);
  }, [openForProduct]);
  useEvent('message', onLookbookQuickViewClick);
  // Shop Layer dispatches its CustomEvent on `document`, not `window`.
  useEvent('syndecaProductClick', onShopLayerProductClick, isBrowser ? document : null);
  const onClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  // Close on navigation, since PDP links (header title, photo gallery) don't
  // close the modal themselves. Keyed on pathname, not hash/query, since
  // lookbook scroll and PDP option selection only change those.
  useEffect(() => {
    setIsOpen(false);
  }, [pathname]);
  useEffect(() => {
    if (!isOpen) {
      setSlug(null);
      setPath(null);
    }
  }, [isOpen]);
  return {
    isOpen,
    onClose,
    path,
    slug,
    productVariation
  };
};
export default useQuickViewModalState;